/* The translation catalogue, assembled and shape-checked.
 *
 * Two things happen here and nowhere else.
 *
 * **Namespaces are declared.** Splitting the catalogue by area keeps any one
 * file small enough to read, and — more usefully — gives shared vocabulary a
 * single home. Queue names, order types and catalog verdicts appear on fifteen
 * screens each; they live in `domain` and are quoted, never copied. A string
 * that two areas need belongs in `common` or `domain`. That rule is the whole
 * defence against the same sentence drifting into three slightly different
 * German translations.
 *
 * **German is checked against English at compile time.** Each German namespace
 * is `satisfies typeof en<Namespace>`, so a key added to English and forgotten
 * in German fails `pnpm typecheck` — which `build` runs — rather than shipping
 * and rendering the raw key to an operator. English is the reference by
 * construction: it is the language the screens were written in, so it is the
 * one that cannot silently fall behind.
 *
 * Adding a namespace is three lines: two imports and one entry in each locale.
 */

import deAdministration from './locales/de/administration.json';
import deApp from './locales/de/app.json';
import deCase from './locales/de/case.json';
import deCommon from './locales/de/common.json';
import deDomain from './locales/de/domain.json';
import deInsight from './locales/de/insight.json';
import deLogin from './locales/de/login.json';
import deMasterData from './locales/de/masterData.json';
import deNav from './locales/de/nav.json';
import dePixie from './locales/de/pixie.json';
import deReview from './locales/de/review.json';
import deRouted from './locales/de/routed.json';
import deTenancy from './locales/de/tenancy.json';
import deTenantGroups from './locales/de/tenantGroups.json';
import deWork from './locales/de/work.json';
import enAdministration from './locales/en/administration.json';
import enApp from './locales/en/app.json';
import enCase from './locales/en/case.json';
import enCommon from './locales/en/common.json';
import enDomain from './locales/en/domain.json';
import enInsight from './locales/en/insight.json';
import enLogin from './locales/en/login.json';
import enMasterData from './locales/en/masterData.json';
import enNav from './locales/en/nav.json';
import enPixie from './locales/en/pixie.json';
import enReview from './locales/en/review.json';
import enRouted from './locales/en/routed.json';
import enTenancy from './locales/en/tenancy.json';
import enTenantGroups from './locales/en/tenantGroups.json';
import enWork from './locales/en/work.json';

/** The namespace a bare key resolves against. `common` holds the vocabulary
    every screen needs, so most `t('…')` calls need no prefix at all. */
export const defaultNS = 'common';

export const resources = {
  en: {
    common: enCommon,
    app: enApp,
    domain: enDomain,
    login: enLogin,
    nav: enNav,
    work: enWork,
    routed: enRouted,
    insight: enInsight,
    review: enReview,
    masterData: enMasterData,
    administration: enAdministration,
    tenancy: enTenancy,
    tenantGroups: enTenantGroups,
    case: enCase,
    pixie: enPixie,
  },
  de: {
    common: deCommon satisfies typeof enCommon,
    app: deApp satisfies typeof enApp,
    domain: deDomain satisfies typeof enDomain,
    login: deLogin satisfies typeof enLogin,
    nav: deNav satisfies typeof enNav,
    work: deWork satisfies typeof enWork,
    routed: deRouted satisfies typeof enRouted,
    insight: deInsight satisfies typeof enInsight,
    review: deReview satisfies typeof enReview,
    masterData: deMasterData satisfies typeof enMasterData,
    administration: deAdministration satisfies typeof enAdministration,
    tenancy: deTenancy satisfies typeof enTenancy,
    tenantGroups: deTenantGroups satisfies typeof enTenantGroups,
    case: deCase satisfies typeof enCase,
    pixie: dePixie satisfies typeof enPixie,
  },
} as const;

/** Every namespace, for i18next's `ns` option. Derived from the English side so
    it cannot drift from what is actually bundled. */
export const namespaces = Object.keys(resources.en) as readonly (keyof typeof resources.en)[];
